import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  OutlinedInput,
  InputAdornment,
  Select,
} from "@mui/material";
import OrderHistoryCard from "./components/OrderHistoryCard";
import SortIcon from "@mui/icons-material/Sort";
import { getUserOrders } from "../../api/order-summary.api";
import "./order-history-page.scss";
import { AlertComponent } from "../../common/components";

const TABS = ["All", "Confirmed", "Completed", "Pending", "Cancelled"];
const TABS_TITLE = ["All", "Confirmed", "Completed", "Pending", "Cancelled"];

const OrderHistoryPage = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [sortOrder, setSortOrder] = useState("Newest");
  const [anchorEl, setAnchorEl] = useState(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "info">(
    "info",
  );

  const sortOptions = [
    { value: "Newest", label: "Newest to Oldest" },
    { value: "Oldest", label: "Oldest to Newest" },
  ];

  const handleTabChange = (_, newValue) => {
    setActiveTab(TABS[newValue]);
  };

  const handleOpenFilter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOrderCancelled = (
    message?: string,
    type: "success" | "error" | "info" = "success",
  ) => {
    if (message) {
      setAlertMessage(message);
      setAlertType(type);
      setAlertOpen(true);
    }

    setTimeout(() => {
      refreshOrders();
    }, 200);
  };

  const handleCloseFilter = (order) => {
    if (order) setSortOrder(order);
    setAnchorEl(null);
  };

  const refreshOrders = async () => {
    try {
      setLoading(true);
      const sortParam = sortOrder === "Newest" ? "newest" : "oldest";
      const response = await getUserOrders(sortParam);
      setOrders(response.data?.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    return activeTab === "All"
      ? orders
      : orders.filter((o) => o && o.status && o.status.toLowerCase() === activeTab.toLowerCase());
  }, [activeTab, orders]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);

        const sortParam = sortOrder === "Newest" ? "newest" : "oldest";

        const response = await getUserOrders(sortParam);
        setOrders(response.data?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [sortOrder]);

  return (
    <Container className="order-history">
      {/* Header */}
      <Box className="order-history__header">
        <Typography className="order-history__title">Order History</Typography>
        <Typography className="order-history__subtitle">
          All your past orders in one place
        </Typography>
      </Box>

      {/* Tabs + Filter */}
      <Box className="order-history__controls">
        <Tabs
          value={TABS.indexOf(activeTab)}
          onChange={handleTabChange}
          className="order-history__tabs"
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          {TABS_TITLE.map((tab) => (
            <Tab key={tab} label={tab} disableRipple />
          ))}
        </Tabs>

        <Select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          IconComponent={() => null}
          className="order-history__filter-btn"
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 1,
              },
            },
          }}
          input={
            <OutlinedInput
              startAdornment={
                <InputAdornment position="start">
                  <SortIcon sx={{ color: "#104145" }} />
                </InputAdornment>
              }
              sx={{
                height: 36,
                fontWeight: 500,
                fontSize: "14px",

                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },

                "& .MuiSelect-select": {
                  padding: "6px",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          }
        >
          {sortOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                color: "#104145",
                "&.Mui-selected": {
                  bgcolor: "#15585D33 !important",
                  color: "#104145",
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => handleCloseFilter()}
        >
          <MenuItem onClick={() => handleCloseFilter("Newest")}>
            Newest to Oldest
          </MenuItem>
          <MenuItem onClick={() => handleCloseFilter("Oldest")}>
            Oldest to Newest
          </MenuItem>
        </Menu>
      </Box>

      {/* Cards */}
      <Box className="order-history__list">
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={4}
          >
            <CircularProgress className="cart-page__circular-progress" />
          </Box>
        ) : filteredOrders?.length > 0 ? (
          filteredOrders?.map((order) => (
            <OrderHistoryCard
              key={order?.id}
              order={order}
              onOrderCancelled={handleOrderCancelled}
            />
          ))
        ) : (
          <Typography mt={4} textAlign="center" color="textSecondary">
            You have no orders yet.
          </Typography>
        )}
      </Box>
      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        autoHideDuration={4000}
        onClose={() => setAlertOpen(false)}
      />
    </Container>
  );
};

export default OrderHistoryPage;
